import React, { useState, useId, useEffect } from "react";
import Plot from 'react-plotly.js';
import "./GraphResult.css";
import ToolTipComponent from "../Tooltip/ToolTipComponent";
import { useToolTip } from "../Tooltip/ToolTip";
import FeedbackPopup from "../FeedbackPopup/Feedbcakpopup";
import FullscreenModal from "./FullscreenModal";

interface GraphResultProps {
    className?: string;
    copyToClipBoard?: (e: any) => void;
    graphData?: any;
}

const GraphResult: React.FC<GraphResultProps> = ({
    className = "",
    copyToClipBoard,
    graphData,
}) => {
    const id = useId();
    const {
        tooltipText,
        position,
        visible,
        showTooltip,
        moveTooltip,
        hideTooltip,
    } = useToolTip();
    const [graphImage, setGraphImage] = useState<string>('');
    const [analysisText, setAnalysisText] = useState<string>('');
    const [plotlyData, setPlotlyData] = useState<any[]>([]);
    const [plotlyLayout, setPlotlyLayout] = useState<any>({});
    const [isPlotlyChart, setIsPlotlyChart] = useState<boolean>(false);
    const [isLike, setLike] = useState<boolean>(false);
    const [isDisLike, setIsDisLike] = useState<boolean>(false);
    const [isDisLikeFlag, setIsDisLikeFlag] = useState<boolean>(false);
    const [isCopy, setCopy] = useState<boolean>(false);
    const [popupPosition, setPopupPosition] = useState({
        top: "0px",
        left: "0px",
    });
    const [hoveredCopy, setHoveredCopy] = useState(false);
    const [hoveredShared, setHoveredShared] = useState(false);
    const [hoveredLiked, setHoveredLiked] = useState(false);
    const [hoveredDis, setHoveredDis] = useState(false);
    // Description action states
    const [descriptionIsLike, setDescriptionLike] = useState<boolean>(false);
    const [descriptionIsDisLike, setDescriptionIsDisLike] =
        useState<boolean>(false);
    const [descriptionIsDisLikeFlag, setDescriptionIsDisLikeFlag] =
        useState<boolean>(false);
    const [descriptionIsCopy, setDescriptionCopy] = useState<boolean>(false);
    const [descriptionPopupPosition, setDescriptionPopupPosition] = useState({
        top: "0px",
        left: "0px",
    });
    const [descriptionHoveredCopy, setDescriptionHoveredCopy] = useState(false);
    const [descriptionHoveredShared, setDescriptionHoveredShared] =
        useState(false);
    const [descriptionHoveredLiked, setDescriptionHoveredLiked] = useState(false);
    const [descriptionHoveredDis, setDescriptionHoveredDis] = useState(false);

    // Edit popup states
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [editPopupPosition, setEditPopupPosition] = useState({
        top: "0px",
        left: "0px",
    });
    const [editFormData, setEditFormData] = useState({
        title: "Graph Title",
        axisY: "Y Axis",
        axisX: "X Axis",
    });
    
    const [isFullscreen, setIsFullscreen] = useState(false);

    const dislikeHandler = (event: React.MouseEvent) => {
        setIsDisLikeFlag(true);
        setIsDisLike(!isDisLike);
        const iconPosition = (event.target as HTMLElement).getBoundingClientRect();
        setPopupPosition({
            top: `${iconPosition.top - 150}px`,
            left: `${iconPosition.left - 30}px`,
        });
    };

    const copyHandler = () => {
        setCopy(true);
        setTimeout(() => {
            setCopy(false);
        }, 1000);
        
        if (isPlotlyChart) {
            // For Plotly charts, convert to image and copy to clipboard
            const plotElement = document.querySelector('.js-plotly-plot') as any;
            if (plotElement && plotElement.data) {
                try {
                    (window as any).Plotly.toImage(plotElement, {
                        format: 'png',
                        width: 800,
                        height: 600
                    }).then((dataUrl: string) => {
                        // Convert data URL to blob and copy to clipboard
                        const base64Data = dataUrl.split(',')[1];
                        const byteCharacters = atob(base64Data);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: 'image/png' });
                        const clipboardItem = new ClipboardItem({
                            'image/png': blob
                        });
                        navigator.clipboard.write([clipboardItem]);
                    }).catch((error: any) => {
                        console.error("Failed to copy Plotly chart:", error);
                    });
                } catch (error) {
                    console.error("Plotly copy not available:", error);
                }
            }
        } else if (copyToClipBoard) {
            copyToClipBoard(null);
        }
    };

    const shareHandler = () => {
        if (isPlotlyChart) {
            const plotElement = document.querySelector('.js-plotly-plot') as any;
            if (plotElement && plotElement.data) {
                try {
                    (window as any).Plotly.toImage(plotElement, {
                        format: 'png',
                        width: 800,
                        height: 600
                    }).then((dataUrl: string) => {
                        const link = document.createElement('a');
                        link.href = dataUrl;
                        link.download = `graph-${Date.now()}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }).catch((error: any) => {
                        console.error("Failed to export Plotly chart:", error);
                    });
                } catch (error) {
                    console.error("Plotly export not available:", error);
                }
            }
        } else if (graphImage) {
            try {
                if (graphImage.startsWith('<')) {
                    console.warn("Cannot download HTML content as image");
                } else {
                    const link = document.createElement('a');
                    link.href = `data:image/png;base64,${graphImage}`;
                    link.download = `graph-${Date.now()}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (error) {
                console.error("Failed to download graph image:", error);
            }
        }
    };

    const likeHandler = () => {
        setLike(!isLike);
    };

    const closePopupAndDislike = () => {
        setIsDisLike(true);
        setIsDisLikeFlag(false);
    };

    const closepopup = () => {
        setIsDisLikeFlag(false);
        setIsDisLike(false);
    };

    const handleEditClick = (event: React.MouseEvent) => {
        const iconPosition = (event.target as HTMLElement).getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;

        setEditPopupPosition({
            top: `${iconPosition.top + scrollTop + 30}px`,
            left: `${iconPosition.left + scrollLeft - 150}px`,
        });
        setShowEditPopup(true);
    };

    const handleEditFormChange = (field: string, value: string) => {
        setEditFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSaveEdit = () => {
        // Update the chart labels with the form data

        console.log("Saving edit data:", editFormData);
        setShowEditPopup(false);
    };

    const handleCloseEditPopup = () => {
        setShowEditPopup(false);
    };

    // Description action handlers
    const descriptionDislikeHandler = (event: React.MouseEvent) => {
        setDescriptionIsDisLikeFlag(true);
        setDescriptionIsDisLike(!descriptionIsDisLike);
        const iconPosition = (event.target as HTMLElement).getBoundingClientRect();
        setDescriptionPopupPosition({
            top: `${iconPosition.top - 150}px`,
            left: `${iconPosition.left - 30}px`,
        });
    };

    const descriptionCopyHandler = () => {
        setDescriptionCopy(true);
        setTimeout(() => {
            setDescriptionCopy(false);
        }, 1000);
        // Copy the description text
        navigator.clipboard.writeText(analysisText);
    };

    const descriptionLikeHandler = () => {
        setDescriptionLike(!descriptionIsLike);
    };

    const descriptionClosePopupAndDislike = () => {
        setDescriptionIsDisLike(true);
        setDescriptionIsDisLikeFlag(false);
    };

    const descriptionClosepopup = () => {
        setDescriptionIsDisLikeFlag(false);
        setDescriptionIsDisLike(false);
    };

    const descriptionShareHandler = () => {
        if (!analysisText) return console.warn("No description text available");
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        // Setup canvas
        Object.assign(canvas, { width: 800, height: 600 });
        Object.assign(ctx, { 
            fillStyle: '#ffffff', 
            textAlign: 'left', 
            textBaseline: 'top' 
        });
        ctx.fillRect(0, 0, 800, 600);
        
        // Add title
        ctx.fillStyle = '#333';
        ctx.font = 'bold 24px Arial';
        ctx.fillText('Graph Description', 40, 40);
        
        // Add wrapped text
        ctx.font = '16px Arial';
        const words = analysisText.split(' ');
        let line = '', y = 100;
        
        words.forEach((word, i) => {
            const testLine = line + word + ' ';
            if (ctx.measureText(testLine).width > 720 && i > 0) {
                ctx.fillText(line, 40, y);
                line = word + ' ';
                y += 24;
            } else {
                line = testLine;
            }
        });
        ctx.fillText(line, 40, y);
        
        // Download
        canvas.toBlob(blob => {
            if (!blob) return;
            const link = Object.assign(document.createElement('a'), {
                href: URL.createObjectURL(blob),
                download: `graph-description-${Date.now()}.png`
            });
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        }, 'image/png');
    };

    useEffect(() => {
        if (graphData) {
            const htmlContent = graphData.graph?.html;
            const base64Content = graphData.graph?.bytes_base64;
            
            if (htmlContent) {
                try {
                    const plotlyMatch = htmlContent.match(/Plotly\.newPlot\s*\(\s*"[^"]+",\s*(\[[\s\S]*?\]),\s*(\{[\s\S]*?\}),?\s*(\{[\s\S]*?\})?\s*\)/);
                    if (plotlyMatch) {
                        const data = JSON.parse(plotlyMatch[1]);
                        const layout = JSON.parse(plotlyMatch[2]);
                        setPlotlyData(data);
                        setPlotlyLayout({
                            ...layout,
                            width: undefined,
                            height: undefined,
                            autosize: true,
                            responsive: true,
                            margin: {
                                l: 60,
                                r: 30,
                                t: 60,
                                b: 80,
                                pad: 4
                            },
                            font: {
                                size: 12
                            }
                        });
                        setIsPlotlyChart(true);
                        setGraphImage('');
                    } else {
                        setGraphImage(htmlContent);
                        setIsPlotlyChart(false);
                    }
                } catch (error) {
                    setGraphImage(htmlContent);
                    setIsPlotlyChart(false);
                }
            } else if (base64Content) {
                setGraphImage(base64Content);
                setIsPlotlyChart(false);
            } else {
                setGraphImage('');
                setIsPlotlyChart(false);
            }
            
            setAnalysisText(graphData.analysis?.text || "");
        }
    }, [graphData]);

    return (
        <div className={`graph-result-container ${className}`}>
            <ToolTipComponent
                text={tooltipText}
                position={position}
                visible={visible}
                marginLeft={70}
                marginTop={25}
                isPositionSet={true}
            />
            <div className="graph-result-content" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                <div className="chart-wrapper" style={{ width: '100%', maxWidth: '100%' }}>
                    <div className="chart-body" style={{ width: '100%', maxWidth: '100%' }}>
                        <div className="chart-content" style={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                            {isPlotlyChart ? (
                                <div style={{ 
                                    width: '100%', 
                                    height: '400px', 
                                    overflow: 'hidden',
                                    position: 'relative',
                                    boxSizing: 'border-box',
                                    cursor: 'pointer'
                                }} onClick={() => setIsFullscreen(true)}>
                                    <div className="fullscreen-icon">
                                        <img
                                            src="svg/fullscreen.svg"
                                            alt="Fullscreen"
                                            width="16"
                                            height="16"
                                        />
                                    </div>
                                    <Plot
                                        data={plotlyData}
                                        layout={{
                                            ...plotlyLayout,
                                            width: undefined,
                                            height: undefined,
                                            autosize: true,
                                            responsive: true,
                                            margin: { l: 50, r: 50, t: 50, b: 50 }
                                        }}
                                        config={{
                                            responsive: true,
                                            displayModeBar: false,
                                            displaylogo: false,
                                            staticPlot: false,
                                            modeBarButtonsToRemove: [
                                                'zoom2d',
                                                'pan2d',
                                                'select2d',
                                                'lasso2d',
                                                'zoomIn2d',
                                                'zoomOut2d',
                                                'autoScale2d',
                                                'resetScale2d',
                                                'hoverClosestCartesian',
                                                'hoverCompareCartesian',
                                                'zoom3d',
                                                'pan3d',
                                                'resetCameraDefault3d',
                                                'resetCameraLastSave3d',
                                                'hoverClosest3d',
                                                'orbitRotation',
                                                'tableRotation',
                                                'zoomInGeo',
                                                'zoomOutGeo',
                                                'resetGeo',
                                                'hoverClosestGeo',
                                                'toImage',
                                                'sendDataToCloud',
                                                'hoverClosestGl2d',
                                                'hoverClosestPie',
                                                'toggleHover',
                                                'resetViews',
                                                'toggleSpikelines',
                                                'resetViewMapbox'
                                            ]
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            height: '100%',
                                            minHeight: '300px',
                                            maxHeight: '400px',
                                            boxSizing: 'border-box'
                                        }}
                                        useResizeHandler={true}
                                    />
                                </div>
                            ) : graphImage ? (
                                graphImage.startsWith('<') ? (
                                    <div style={{
                                        position: 'relative',
                                        cursor: 'pointer'
                                    }} onClick={() => setIsFullscreen(true)}>
                                        <div className="fullscreen-icon">
                                            <img
                                                src="svg/fullscreen.svg"
                                                alt="Fullscreen"
                                                width="16"
                                                height="16"
                                            />
                                        </div>
                                        <iframe
                                            srcDoc={graphImage}
                                            style={{ 
                                                width: '100%', 
                                                height: '400px', 
                                                border: 'none'
                                            }}
                                            title="Graph Chart"
                                            sandbox="allow-scripts allow-same-origin"
                                        />
                                    </div>
                                ) : (
                                    <div style={{
                                        position: 'relative',
                                        cursor: 'pointer'
                                    }} onClick={() => setIsFullscreen(true)}>
                                        <div className="fullscreen-icon">
                                            <img
                                                src="svg/fullscreen.svg"
                                                alt="Fullscreen"
                                                width="16"
                                                height="16"
                                            />
                                        </div>
                                        <img
                                            src={`data:image/png;base64,${graphImage}`}
                                            className="chart-image"
                                            alt="Generated Chart"
                                            style={{ maxWidth: '100%', height: 'auto' }}
                                        />
                                    </div>
                                )
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    No chart data available
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <FullscreenModal
                    isOpen={isFullscreen}
                    onClose={() => setIsFullscreen(false)}
                    content={
                        isPlotlyChart
                            ? {
                                type: 'plotly',
                                data: plotlyData,
                                layout: plotlyLayout
                            }
                            : graphImage
                                ? graphImage.startsWith('<')
                                    ? { type: 'iframe', srcDoc: graphImage }
                                    : { type: 'image', src: `data:image/png;base64,${graphImage}` }
                                : { type: 'image', src: '' }
                    }
                />

                <div className="graph-result-actions">
                    <div
                        className="footer chatfootericon"
                        style={{ position: "relative" }}
                    >
                        <div className="icon-left">
                            {isCopy ? (
                                <img
                                    className="icon tick"
                                    width={"25"}
                                    height={"25"}
                                    alt="copy confirmed"
                                    src={"svg/action-icons/copy confirmed.svg"}
                                    onMouseEnter={(event) => showTooltip("Copy", event)}
                                    onMouseMove={(event) => moveTooltip(event)}
                                    onMouseLeave={hideTooltip}
                                />
                            ) : (
                                <span onClick={copyHandler}>
                                    <img
                                        className="icon"
                                        width={"13"}
                                        height={"13"}
                                        alt="copy"
                                        src={hoveredCopy ? "svg/hover/Copy.svg" : "svg/Copy.svg"}
                                        onMouseMove={(event) => moveTooltip(event)}
                                        onMouseLeave={() => {
                                            hideTooltip();
                                            setHoveredCopy(false);
                                        }}
                                    />
                                </span>
                            )}

                            <span onClick={shareHandler}>
                                <img
                                    className="icon"
                                    width={"13"}
                                    height={"13"}
                                    alt="download"
                                    src={hoveredShared ? "svg/hover/download.svg" : "svg/download.svg"}
                                    onMouseMove={(event) => moveTooltip(event)}
                                    onMouseLeave={() => {
                                        hideTooltip();
                                        setHoveredShared(false);
                                    }}
                                />
                            </span>

                            <span onClick={likeHandler}>
                                {isLike ? (
                                    <img
                                        className="icon tick"
                                        width={"13"}
                                        height={"13"}
                                        alt="like confirmed"
                                        src={"svg/action-icons/Thumbsup confirm.svg"}
                                        onMouseMove={(event) => moveTooltip(event)}
                                        onMouseLeave={hideTooltip}
                                    />
                                ) : (
                                    <img
                                        className="icon"
                                        width={"13"}
                                        height={"13"}
                                        alt="like"
                                        src={
                                            hoveredLiked
                                                ? "svg/hover/Thumbsup.svg"
                                                : "svg/Thumbsup.svg"
                                        }
                                        onMouseMove={(event) => moveTooltip(event)}
                                        onMouseLeave={() => {
                                            hideTooltip();
                                            setHoveredLiked(false);
                                        }}
                                    />
                                )}
                            </span>

                            <span onClick={dislikeHandler}>
                                {isDisLike ? (
                                    <img
                                        data-id={id}
                                        className="icon tick"
                                        width={"13"}
                                        height={"13"}
                                        alt="Dislike active"
                                        src={"svg/action-icons/Thumbsdown confirm.svg"}
                                        onMouseMove={(event) => moveTooltip(event)}
                                        onMouseLeave={hideTooltip}
                                    />
                                ) : (
                                    <img
                                        data-id={id}
                                        className="icon"
                                        width={"13"}
                                        height={"13"}
                                        alt="Dislike"
                                        src={
                                            hoveredDis
                                                ? "svg/hover/Thumbsdown.svg"
                                                : "svg/Thumbsdown.svg"
                                        }
                                        onMouseMove={(event) => moveTooltip(event)}
                                        onMouseLeave={() => {
                                            hideTooltip();
                                            setHoveredDis(false);
                                        }}
                                    />
                                )}
                            </span>

                            <span onClick={handleEditClick}>
                                <img
                                    className="icon"
                                    width={"18"}
                                    height={"18"}
                                    alt="edit"
                                    src={"svg/editIcon.svg"}
                                    onMouseMove={(event) => moveTooltip(event)}
                                    onMouseLeave={() => {
                                        hideTooltip();
                                    }}
                                />
                            </span>

                            <div>
                                {isDisLike && isDisLikeFlag ? (
                                    <FeedbackPopup
                                        isVisible={isDisLikeFlag && isDisLike}
                                        onClose={closepopup}
                                        onCloseAndDislike={closePopupAndDislike}
                                        position={popupPosition}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="describe-section">

                    {analysisText && (
                        <div className="graph-description">
                            <div className="description-content">
                                <div className="description-header">
                                    <h3>Graph Description</h3>
                                </div>
                                <div className="description-text">
                                    <p>
                                        {analysisText}
                                    </p>
                                </div>

                            </div>
                            <div className="description-actions">
                                <div
                                    className="footer chatfootericon"
                                    style={{ position: "relative" }}
                                >
                                    <div className="icon-left">
                                        {descriptionIsCopy ? (
                                            <img
                                                className="icon tick"
                                                width={"25"}
                                                height={"25"}
                                                alt="copy confirmed"
                                                src={"svg/action-icons/copy confirmed.svg"}
                                                onMouseEnter={(event) => showTooltip("Copy", event)}
                                                onMouseMove={(event) => moveTooltip(event)}
                                                onMouseLeave={hideTooltip}
                                            />
                                        ) : (
                                            <span onClick={descriptionCopyHandler}>
                                                <img
                                                    className="icon"
                                                    width={"13"}
                                                    height={"13"}
                                                    alt="copy"
                                                    src={
                                                        descriptionHoveredCopy
                                                            ? "svg/hover/Copy.svg"
                                                            : "svg/Copy.svg"
                                                    }
                                                    onMouseMove={(event) => moveTooltip(event)}
                                                    onMouseLeave={() => {
                                                        hideTooltip();
                                                        setDescriptionHoveredCopy(false);
                                                    }}
                                                />
                                            </span>
                                        )}

                                        <span onClick={descriptionShareHandler}>
                                            <img
                                                className="icon"
                                                width={"13"}
                                                height={"13"}
                                                alt="download"
                                                src={
                                                    descriptionHoveredShared
                                                        ? "svg/hover/download.svg"
                                                        : "svg/download.svg"
                                                }
                                                onMouseMove={(event) => moveTooltip(event)}
                                                onMouseLeave={() => {
                                                    hideTooltip();
                                                    setDescriptionHoveredShared(false);
                                                }}
                                            />
                                        </span>

                                        <span onClick={descriptionLikeHandler}>
                                            {descriptionIsLike ? (
                                                <img
                                                    className="icon tick"
                                                    width={"13"}
                                                    height={"13"}
                                                    alt="like confirmed"
                                                    src={"svg/action-icons/Thumbsup confirm.svg"}
                                                    onMouseEnter={(event) =>
                                                        showTooltip("Positive feedback", event)
                                                    }
                                                    onMouseMove={(event) => moveTooltip(event)}
                                                    onMouseLeave={hideTooltip}
                                                />
                                            ) : (
                                                <img
                                                    className="icon"
                                                    width={"13"}
                                                    height={"13"}
                                                    alt="like"
                                                    src={
                                                        descriptionHoveredLiked
                                                            ? "svg/hover/Thumbsup.svg"
                                                            : "svg/Thumbsup.svg"
                                                    }
                                                    onMouseMove={(event) => moveTooltip(event)}
                                                    onMouseLeave={() => {
                                                        hideTooltip();
                                                        setDescriptionHoveredLiked(false);
                                                    }}
                                                />
                                            )}
                                        </span>

                                        <span onClick={descriptionDislikeHandler}>
                                            {descriptionIsDisLike ? (
                                                <img
                                                    data-id={id + "-desc"}
                                                    className="icon tick"
                                                    width={"13"}
                                                    height={"13"}
                                                    alt="Dislike active"
                                                    src={"svg/action-icons/Thumbsdown confirm.svg"}
                                                    onMouseEnter={(event) =>
                                                        showTooltip("Negative feedback", event)
                                                    }
                                                    onMouseMove={(event) => moveTooltip(event)}
                                                    onMouseLeave={hideTooltip}
                                                />
                                            ) : (
                                                <img
                                                    data-id={id + "-desc"}
                                                    className="icon"
                                                    width={"13"}
                                                    height={"13"}
                                                    alt="Dislike"
                                                    src={
                                                        descriptionHoveredDis
                                                            ? "svg/hover/Thumbsdown.svg"
                                                            : "svg/Thumbsdown.svg"
                                                    }
                                                    onMouseMove={(event) => moveTooltip(event)}
                                                    onMouseLeave={() => {
                                                        hideTooltip();
                                                        setDescriptionHoveredDis(false);
                                                    }}
                                                />
                                            )}
                                        </span>

                                        <div>
                                            {descriptionIsDisLike && descriptionIsDisLikeFlag ? (
                                                <FeedbackPopup
                                                    isVisible={
                                                        descriptionIsDisLikeFlag && descriptionIsDisLike
                                                    }
                                                    onClose={descriptionClosepopup}
                                                    onCloseAndDislike={descriptionClosePopupAndDislike}
                                                    position={descriptionPopupPosition}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Edit Popup */}
                {showEditPopup && (
                    <>
                        <div
                            className="edit-popup-backdrop"
                            onClick={handleCloseEditPopup}
                        ></div>
                        <div className="edit-popup-positioned" style={editPopupPosition}>
                            <div className="edit-form">
                                <div className="graphform-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        value={editFormData.title}
                                        onChange={(e) =>
                                            handleEditFormChange("title", e.target.value)
                                        }
                                        placeholder="Graph title"
                                    />
                                </div>
                                <div className="graphform-group">
                                    <label>Axis Y</label>
                                    <input
                                        type="text"
                                        value={editFormData.axisY}
                                        onChange={(e) =>
                                            handleEditFormChange("axisY", e.target.value)
                                        }
                                        placeholder="Y axis"
                                    />
                                </div>
                                <div className="graphform-group">
                                    <label>Axis X</label>
                                    <input
                                        type="text"
                                        value={editFormData.axisX}
                                        onChange={(e) =>
                                            handleEditFormChange("axisX", e.target.value)
                                        }
                                        placeholder="X axis"
                                    />
                                </div>
                                <div className="graphform-actions">
                                    <button className="save-btn" onClick={handleSaveEdit}>
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default GraphResult;
